import { NetworkStatus, useMutation, useQuery } from "@apollo/client";

import client from "#/src/apollo/_client";
import {
  BULK_DELETE_CUSTOMER_PORTALS_MUTATION,
  CREATE_CUSTOMER_PORTAL_MUTATION,
  DELETE_CUSTOMER_PORTAL_MUTATION,
  GET_CUSTOMER_PORTAL_QUERY,
  LIST_CUSTOMER_PORTALS_QUERY,
  LIST_OWN_OEM_PORTAL_HISTORY_QUERY,
  UPDATE_CUSTOMER_PORTAL_MUTATION,
} from "~/api";
import { ITEMS_PER_PAGE } from "~/constants";
import {
  CREATE_CUSTOMER_PORTAL,
  DELETE_CUSTOMER_PORTAL,
  GET_CUSTOMER_PORTAL,
  LIST_CUSTOMER_PORTALS,
  LIST_OWN_OEM_PORTAL_HISTORY,
  UPDATE_CUSTOMER_PORTAL,
} from "~/services/customerPortal/constants";
import type { CustomerPortalType } from "~/types/customerPortal";
import { evictHierarchyCaches } from "~/utils/graphql";

export const useCreateCustomerPortal = () => {
  const [create, { error, loading }] = useMutation<
    { [CREATE_CUSTOMER_PORTAL]: CustomerPortalType },
    {
      connectionId: string;
      assignAssets?: boolean;
    }
  >(CREATE_CUSTOMER_PORTAL_MUTATION, {
    update(cache, _result, { variables }) {
      // Creating a portal cascades sub portals across the company's subtree, so
      // `hasCustomerPortal` moves for more companies than the one selected.
      evictHierarchyCaches(cache, [variables?.connectionId]);
    },
  });

  return {
    createCustomerPortal: create,
    error,
    loading,
  };
};

export const useGetCustomerPortal = (id: string) => {
  const {
    data: { getCustomerPortal = {} as CustomerPortalType } = {},
    loading,
    error,
  } = useQuery<{ [GET_CUSTOMER_PORTAL]: CustomerPortalType }>(
    GET_CUSTOMER_PORTAL_QUERY,
    {
      variables: { id },
    },
  );

  return {
    loading,
    error,
    portal: getCustomerPortal,
  };
};

export const useUpdateCustomerPortal = () => {
  const [update, { error, loading }] = useMutation<
    { [UPDATE_CUSTOMER_PORTAL]: CustomerPortalType },
    {
      input: {
        _id: string;
        assetAccess: {
          documentation: boolean;
          parts: boolean;
          preventiveMaintenance: boolean;
          qrCodes: boolean;
          history: boolean;
        };
        productAccess: {
          allowCopying: boolean;
          documentation: boolean;
          parts: boolean;
          procedures: boolean;
        };
        allowInvitingMembers: boolean;
        childPortalAssetVisibility?: boolean;
        allowSubPortalCreation?: boolean;
        allowTicketReroute?: boolean;
        allowSupportTicketCreation: boolean;
      };
    }
  >(UPDATE_CUSTOMER_PORTAL_MUTATION, {
    update(cache, _result, { variables }) {
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: LIST_CUSTOMER_PORTALS,
      });

      // Changing sub portal creation reaches portals below this one in BOTH
      // directions, and none of them are in the mutation response: revoking it
      // cascades the value down, and granting it makes the level below editable
      // again. Either way their cached copies would be wrong until a reload.
      // The query is cache-first, so evicting the root field is what forces the
      // next portal page to go back to the server.
      //
      // Only this toggle sends the field, so no other save pays for the refetch.
      // Ticket rerouting needs none of this: each grant is independent, so the
      // response carries everything that changed.
      if (variables?.input?.allowSubPortalCreation !== undefined) {
        cache.evict({ id: "ROOT_QUERY", fieldName: GET_CUSTOMER_PORTAL });
      }

      cache.gc();
    },
  });

  return {
    updateCustomerPortal: update,
    error,
    loading,
  };
};

export const useDeleteCustomerPortal = () => {
  const [deleteCustomerPortal, { error, loading }] = useMutation<
    { [DELETE_CUSTOMER_PORTAL]: CustomerPortalType["_id"] },
    {
      id: string;
    }
  >(DELETE_CUSTOMER_PORTAL_MUTATION, {
    update(cache) {
      // Deleting a root portal cascades to its sub portals, so every company in
      // the branch loses its portal — drop the company caches as well.
      evictHierarchyCaches(cache);
    },
  });

  return {
    deleteCustomerPortal,
    error,
    loading,
  };
};

export const useListCustomerPortals = ({
  limit = ITEMS_PER_PAGE,
  skip = 0,
  searchQuery = "",
  skipCondition = false,
  orgToLink = "",
  userToLink = "",
  connectionId = "",
  parentConnectionId = "",
  fetchPolicy = "cache-and-network" as const,
}: {
  limit?: number;
  skip?: number;
  searchQuery?: string;
  skipCondition?: boolean;
  orgToLink?: string;
  userToLink?: string;
  connectionId?: string;
  parentConnectionId?: string;
  fetchPolicy?: "cache-and-network" | "network-only" | "cache-first";
} = {}) => {
  const variables = {
    params: {
      limit,
      skip,
      where: {
        searchQuery,
        orgToLink,
        userToLink,
        connectionId,
        parentConnectionId,
      },
    },
  };

  const { data, loading, networkStatus, refetch, fetchMore } = useQuery<
    {
      [LIST_CUSTOMER_PORTALS]: {
        currentPage: number;
        limit: number;
        skip: number;
        totalCount: number;
        isExistingContactLinkable: boolean;
        isConnectionAlreadyEstablished: boolean;
        customerPortals: CustomerPortalType[];
      };
    },
    {
      params: {
        limit: number;
        skip: number;
        where: {
          searchQuery: string;
        };
      };
    }
  >(LIST_CUSTOMER_PORTALS_QUERY, {
    variables,
    fetchPolicy,
    notifyOnNetworkStatusChange: true,
    skip: skipCondition,
  });

  const {
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    isExistingContactLinkable,
    isConnectionAlreadyEstablished,
    customerPortals = [],
  } = data?.[LIST_CUSTOMER_PORTALS] ?? {
    currentPage: 0,
    limit: 0,
    skip: 0,
    totalCount: 0,
    customerPortals: [],
  };

  const handleFetchMore = ({
    limit,
    skip,
  }: {
    limit: number;
    skip: number;
  }): void => {
    fetchMore({
      variables: {
        params: {
          limit,
          skip,
          where: {
            searchQuery,
            orgToLink,
            userToLink,
            connectionId,
            parentConnectionId,
          },
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return previousResult;
        }

        const mergedPortals = [
          ...previousResult[LIST_CUSTOMER_PORTALS].customerPortals,
          ...fetchMoreResult[LIST_CUSTOMER_PORTALS].customerPortals,
        ];

        return {
          ...previousResult,
          [LIST_CUSTOMER_PORTALS]: {
            ...previousResult[LIST_CUSTOMER_PORTALS],
            ...fetchMoreResult[LIST_CUSTOMER_PORTALS],
            customerPortals: mergedPortals,
          },
        };
      },
    });
  };

  return {
    customerPortals,
    handleFetchMore,
    refetch,
    isExistingContactLinkable,
    isConnectionAlreadyEstablished,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    loading,
    /**
     * `notifyOnNetworkStatusChange` makes `loading` true for a `fetchMore` as
     * well as a first load, so callers that show a full-page loader need to
     * tell the two apart.
     */
    isFetchingMore: networkStatus === NetworkStatus.fetchMore,
  };
};

export const updateCustomerPortalCache = (payload: CustomerPortalType) => {
  const portal: { [GET_CUSTOMER_PORTAL]: CustomerPortalType } =
    client.cache.readQuery({
      query: GET_CUSTOMER_PORTAL_QUERY,
      variables: { id: payload._id },
    });

  client.cache.writeQuery({
    query: GET_CUSTOMER_PORTAL_QUERY,
    variables: { id: payload._id },
    data: {
      getCustomerPortal: {
        ...portal[GET_CUSTOMER_PORTAL],
        ...payload,
      },
    },
  });
};

export const useBulkDeleteCustomerPortals = () => {
  const [bulkDelete] = useMutation(BULK_DELETE_CUSTOMER_PORTALS_MUTATION);

  const bulkDeletePortals = async (ids: string[]) => {
    const { data } = await bulkDelete({
      variables: { ids },
      update(cache) {
        cache.evict({ id: "ROOT_QUERY", fieldName: LIST_CUSTOMER_PORTALS });
        cache.gc();
      },
    });
    return data.bulkDeleteCustomerPortals;
  };

  return { bulkDeletePortals };
};

/**
 * One portal's History feed — the work orders and support tickets raised for or
 * from that portal.
 *
 * Paginated by appending, like the company history it sits beside: the tab is
 * an infinite-scroll feed, so a page replacing its predecessor would reset the
 * scroll position on every `Waypoint` hit.
 */
export const useListOwnOemPortalHistory = ({
  portalId,
  limit = ITEMS_PER_PAGE,
  skip = 0,
}: {
  portalId?: string;
  limit?: number;
  skip?: number;
}) => {
  const variables = { params: { limit, skip, where: { portal: portalId } } };

  const { data, loading, refetch, fetchMore } = useQuery(
    LIST_OWN_OEM_PORTAL_HISTORY_QUERY,
    {
      variables,
      // Nothing to ask for without a portal, and `where.portal` absent would read
      // as "no filter" rather than "no portal".
      skip: !portalId,
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    },
  );

  const {
    currentPage = 1,
    limit: itemLimit = 0,
    skip: itemSkip = 0,
    totalCount = 0,
    history = [],
    ticketCount = 0,
    supportTicketCount = 0,
  } = data?.[LIST_OWN_OEM_PORTAL_HISTORY] ?? {};

  const handleFetchMore = ({ limit: nextLimit, skip: nextSkip }) => {
    fetchMore({
      variables: {
        params: {
          limit: nextLimit,
          skip: nextSkip,
          where: { portal: portalId },
        },
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;

        return {
          ...previousResult,
          [LIST_OWN_OEM_PORTAL_HISTORY]: {
            ...previousResult[LIST_OWN_OEM_PORTAL_HISTORY],
            ...fetchMoreResult[LIST_OWN_OEM_PORTAL_HISTORY],
            history: [
              ...previousResult[LIST_OWN_OEM_PORTAL_HISTORY].history,
              ...fetchMoreResult[LIST_OWN_OEM_PORTAL_HISTORY].history,
            ],
          },
        };
      },
    });
  };

  return {
    history,
    handleFetchMore,
    refetch,
    currentPage,
    limit: itemLimit,
    skip: itemSkip,
    totalCount,
    ticketCount,
    supportTicketCount,
    loading,
  };
};
